import { push } from 'connected-react-router';
import memoizeOne from 'memoize-one';
import QueryString from 'query-string';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { Query, VirtualBrand } from '~/Api/Analytics/VirtualBrand';
import Pagination from '~/Api/Pagination/Pagination';
import { isProductAtcValid } from '~/Api/Product';
import config from '~/config';
import { fetchCurrentCustomerDatasourceLicencesIfNeeded } from '~/CustomerDatasourceLicence';
import FilterParameter from '~/libs/FilterParameter/FilterParameter';
import FilterParameterDescription from '~/libs/FilterParameter/FilterParameterDescription';
import { fetchDataSources } from '~/MedicinalBrandsAnalytics/DataSource';
import { fetchRegions } from '~/MedicinalBrandsAnalytics/Region';
import {
	exportVirtualBrands,
	fetchMoreVirtualBrands,
	selectVirtualBrands,
	State as VirtualBrandState,
} from '~/MedicinalBrandsAnalytics/VirtualBrand';
import { pruneAll as pruneReimbursement } from '~/MedicinalBrandsAnalytics/VirtualBrand/Reimbursement';
import { pruneAll as pruneTimeToPrice } from '~/MedicinalBrandsAnalytics/VirtualBrand/TimeToPrice';
import { ThunkDispatch } from '~/Store';

import Page from './Page';
import downloadFileFromUrl from '~/libs/Download/downloadFileFromUrl';
import { flashSuccessMessage } from '~/FlashMessage';

import { fetchStatuses } from '~/DataSourceStatus';
import { Result } from '~/Api/DataSourceStatus/findStatuses';

interface State {
	medicinalBrandsAnalyticsVirtualBrand: VirtualBrandState;
}
interface OwnProps {
	filter?: string[];
	currency: string | null;
}

function buildUrl(query: Query): { pathname: string; search: string } {
	return {
		pathname: '/analytics/',
		search: QueryString.stringify({
			filter: query.filterParameters.map((parameter) => {
				if (typeof parameter.value === 'string') {
					return `${parameter.name}:${parameter.value.trim()}`;
				}
				if (parameter.value instanceof Array) {
					return `${parameter.name}:${Array.from(parameter.value).join(';')}`;
				}
				return '';
			}),
			currency: query.currency === config.analytics.defaultCurrency ? undefined : query.currency,
		}),
	};
}

function search(query: Query) {
	return (dispatch: ThunkDispatch<AnyAction>) => {
		dispatch(push(buildUrl(query)));
	};
}

const parseFilterParameters = memoizeOne(
	(
		filter: string[],
		availableParameters: FilterParameterDescription<string[]>[]
	): FilterParameter<string | string[]>[] => {
		const filterParameters = (Array.isArray(filter) ? filter : [filter]).map((parameterPair) => {
			const parts = parameterPair.match(/^([^:]+):(.*)$/);
			if (parts === null) {
				return null;
			}

			const parameter = availableParameters.find((parameter) => parameter.name === parts[1]);
			if (!parameter) {
				return null;
			}

			const value = parameter.parseValueFromString(parts[2], ';');
			if (value === null) {
				return null;
			}

			const normalizedValue = Array.isArray(value) ? value : [value];
			return new FilterParameter(
				parameter.name,
				parameter.name === 'atc' ? normalizedValue.filter(isProductAtcValid) : value
			);
		});

		return filterParameters.filter((parameter): parameter is FilterParameter<string | string[]> => parameter !== null);
	}
);

function parseQuery(
	filter: string[],
	availableParameters: FilterParameterDescription<string[]>[],
	currency: string | null
): Query<string | string[]> {
	const sanitizedCurrency = currency
		? currency === 'source'
			? null
			: config.analytics.currencies.find((allowedCurrency) => allowedCurrency === currency)
		: config.analytics.defaultCurrency;
	return new Query<string | string[]>(parseFilterParameters(filter, availableParameters), sanitizedCurrency || null);
}

function mapStateToProps(
	{ medicinalBrandsAnalyticsVirtualBrand }: State,
	{ filter, currency }: OwnProps
): {
	availableParameters: FilterParameterDescription<string[]>[];
	currencies: string[];
	query: Query<string | string[]>;
	isFetching: boolean;
	virtualBrands: VirtualBrand[] | null;
	pagination: Pagination | null;
} {
	const availableParameters = [
		new FilterParameterDescription<string[]>('brand', 'Brand', false, 'text', []),
		new FilterParameterDescription<string[]>('atc', 'ATC/Active substance', false, 'text', []),
		new FilterParameterDescription<string[]>('therapeutic-area', 'Therapeutic area', false, 'text', []),
	];
	const query = parseQuery(filter || [], availableParameters, currency);
	const virtualBrandsState = selectVirtualBrands(medicinalBrandsAnalyticsVirtualBrand, query);

	return {
		availableParameters,
		currencies: config.analytics.currencies,
		query,
		isFetching: virtualBrandsState?.isFetching || false,
		virtualBrands: virtualBrandsState?.items || null,
		pagination: virtualBrandsState?.pagination || null,
	};
}

function load(query: Query) {
	return (dispatch: ThunkDispatch<AnyAction>) => {
		dispatch(fetchRegions());
		dispatch(fetchDataSources());
		dispatch(fetchCurrentCustomerDatasourceLicencesIfNeeded());
		dispatch(unload());
		if (query.isValid) {
			dispatch(fetchMoreVirtualBrands(query));
		}
	};
}

function unload() {
	return (dispatch: ThunkDispatch<AnyAction>) => {
		dispatch(pruneTimeToPrice());
		dispatch(pruneReimbursement());
	};
}

const actions = {
	onSearch: search,
	onLoad: load,
	onFetchDataSourceStatuses: () => async (dispatch: ThunkDispatch<AnyAction>) => {
		return await (dispatch(fetchStatuses()) as Promise<Result | null>);
	},
	onUnload: unload,
	onExport: (query: Query) => async (dispatch: ThunkDispatch<AnyAction>) => {
		const response = await dispatch(exportVirtualBrands(query));
		if (response) {
			if (response.export.downloadUrl !== null) {
				downloadFileFromUrl(response.export.downloadUrl);
			} else if (response.export.asyncNotification !== null) {
				dispatch(
					flashSuccessMessage(
						`Your export request has been enqueued and once it finishes, you'll receive a notification at your email
                        "${response.export.asyncNotification.email}" with a download link.`
					)
				);
			}
		}
	},
};

export default connect(mapStateToProps, actions)(Page);
